import { useMemo, useState } from "react";
import { X, Loader2, Plus, Calendar, MapPin, BookOpen, GraduationCap, Users, Wallet, Trash2, Globe2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isValidPhoneNumber, countryOf } from "../../lib/phone";
import { useMasterList } from "../../lib/useMasterList";
import PhoneInputE164 from "../PhoneInputE164";
import SearchableSelect from "../SearchableSelect";
import MultiSelectChips from "../MultiSelectChips";
import DataCompletenessMeter from "../DataCompletenessMeter";
import {
    PREFERRED_DAYS, PREFERRED_TIME_WINDOWS, LEARNING_LEVELS, leadCompleteness,
} from "../../lib/aiMatchFields";

const FALLBACK_BOARDS = ["CBSE", "ICSE", "State Board", "IB", "IGCSE", "Cambridge A-Level", "Others"];
const FALLBACK_GRADES = [
    "Pre-K", "Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Class 6", "Class 7",
    "Class 8", "Class 9", "Class 10", "Class 11", "Class 12", "Undergraduate", "Other",
];
const FALLBACK_SUBJECTS = [
    "Mathematics", "Science", "Physics", "Chemistry", "Biology", "English",
    "Hindi", "Social Studies", "History", "Geography", "Economics", "Accountancy",
    "Business Studies", "Computer Science", "Sanskrit", "French", "EVS",
];
const MODES = ["Home Tuition", "Online", "Both"];
const STATUSES = ["new", "contacted", "in_progress", "converted", "lost"];
const BATCH_TYPES = ["1:1 Individual", "Sibling Batch", "Group of 2", "Group of 3+"];
const DURATIONS = [
    { v: 45, label: "45 minutes" },
    { v: 60, label: "60 minutes (1 hour)" },
    { v: 75, label: "75 minutes" },
    { v: 90, label: "90 minutes (1.5 hours)" },
    { v: 120, label: "120 minutes (2 hours)" },
];
const FEE_TYPES = [
    { v: "monthly", label: "Monthly" },
    { v: "session_wise", label: "Session-wise / Per Class" },
    { v: "full_course", label: "Full Course / Package" },
];
const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = ["00", "15", "30", "45"];

const ALL_MAJOR_SUBJECTS = "All Major Subjects";

function emptyStudent() {
    return { name: "", grade: "", board: "", subjects: [], preferred_tutor_gender: "any", batch_type: "1:1 Individual", class_duration_minutes: 60, preferred_timing: "" };
}

/**
 * CreateDemoLeadModal — admin-side "Create Demo Lead" form (V16).
 *
 * Supports multi-student sibling enquiries under one parent lead, structured
 * address for proximity matching, fee information, batch type, class duration
 * and tutor gender preference. Backward compatible — single-student demos
 * continue to work without setting `additional_students`.
 */
export default function CreateDemoLeadModal({ open, onClose, onCreated }) {
    const { items: masterSubjects } = useMasterList("subject", { fallback: FALLBACK_SUBJECTS });
    const { items: masterGrades } = useMasterList("grade", { fallback: FALLBACK_GRADES });
    const { items: masterBoards } = useMasterList("board", { fallback: FALLBACK_BOARDS });
    const { items: countries } = useMasterList("country");
    const { items: languageItems } = useMasterList("language");
    const { items: examItems } = useMasterList("exam_category");
    const { items: currItems } = useMasterList("curriculum_type");
    const SUBJECTS = masterSubjects.map((s) => s.value);
    const GRADES = masterGrades.map((g) => g.value);
    const BOARDS = masterBoards.map((b) => b.value);
    // Primary-grade lookup — used by the All Major Subjects chip per student.
    const primaryGrades = new Set(
        masterGrades.filter((g) => g.extra?.is_primary).map((g) => g.value),
    );
    const studentSubjectOptions = (studentGrade) => (
        primaryGrades.has(studentGrade) ? [ALL_MAJOR_SUBJECTS, ...SUBJECTS] : SUBJECTS
    );
    // Cascading dropdown IDs — null when not chosen yet
    const [addrCountryId, setAddrCountryId] = useState("");
    const [addrStateId, setAddrStateId] = useState("");
    const [addrCityId, setAddrCityId] = useState("");
    const [addrPincodeId, setAddrPincodeId] = useState("");
    const { items: states } = useMasterList("state", { parentId: addrCountryId || null });
    const { items: cities } = useMasterList("city", { parentId: addrStateId || null });
    const { items: pincodes } = useMasterList("pincode", { parentId: addrCityId || null });
    const [form, setForm] = useState({
        parent_name: "", email: "", phone: "",
        mode: "Home Tuition",
        // Demo schedule (12-hour parts collapse into a single ISO datetime on submit)
        preferred_demo_date: "",
        preferred_demo_hour: "5",
        preferred_demo_minute: "00",
        preferred_demo_ampm: "PM",
        // Address
        address_house_no: "", address_area: "", address_landmark: "",
        city: "", state: "", pin_code: "",
        // Fee
        quoted_fee: "", tutor_in_hand_fee: "", fee_type: "monthly",
        // Pipeline + lead meta
        status: "new", lead_type: "demo",
        special_notes: "",
        additional_requirements: "",
        // V24 — AI-first optional fields
        preferred_time_window: "",
        exam_goal: "",
        curriculum_preference: "",
        learning_level: "",
        special_requirements: "",
        // Students: primary + optional siblings (we always start with one student)
        students: [emptyStudent()],
    });
    // V24 — Multi-select AI fields
    const [languagePreference, setLanguagePreference] = useState([]);
    const [preferredDays, setPreferredDays] = useState([]);
    const [aiOpen, setAiOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const updateStudent = (idx, k, v) => setForm((f) => ({
        ...f,
        students: f.students.map((s, i) => {
            if (i !== idx) return s;
            const next = { ...s, [k]: v };
            // If grade changes to non-primary, strip the synthetic "All Major Subjects" chip
            if (k === "grade" && !primaryGrades.has(v) && next.subjects.includes(ALL_MAJOR_SUBJECTS)) {
                next.subjects = next.subjects.filter((x) => x !== ALL_MAJOR_SUBJECTS);
            }
            return next;
        }),
    }));
    const toggleStudentSubject = (idx, subj) => setForm((f) => ({
        ...f,
        students: f.students.map((s, i) => {
            if (i !== idx) return s;
            const set = new Set(s.subjects);
            if (set.has(subj)) { set.delete(subj); } else { set.add(subj); }
            return { ...s, subjects: Array.from(set) };
        }),
    }));
    const addStudent = () => setForm((f) => ({ ...f, students: [...f.students, emptyStudent()] }));
    const removeStudent = (idx) => setForm((f) => ({
        ...f,
        students: f.students.length <= 1 ? f.students : f.students.filter((_, i) => i !== idx),
    }));

    if (!open) return null;

    // V24 — Live completeness % for the AI meter (uses primary student + root form)
    const completenessPct = (() => {
        const primary = form.students[0] || {};
        return leadCompleteness({
            name: primary.name,
            grade: primary.grade,
            subjects: (primary.subjects || []).join(", "),
            board: primary.board,
            mode: form.mode,
            country: countries.find((c) => c.id === addrCountryId)?.value,
            state: form.state, city: form.city, pin_code: form.pin_code,
            preferred_tutor_gender: primary.preferred_tutor_gender,
            class_duration_minutes: primary.class_duration_minutes,
            language_preference: languagePreference,
            preferred_days: preferredDays,
            preferred_time_window: form.preferred_time_window,
            exam_goal: form.exam_goal,
            curriculum_preference: form.curriculum_preference,
            learning_level: form.learning_level,
            quoted_fee: form.quoted_fee,
        });
    })();

    // V19 — Mode-driven country logic (tightens the V18 country-driven rule):
    //   Home Tuition is India-only. The country dropdown is restricted to
    //   India and we auto-select it. Online / Both → all countries are visible.
    const isHomeMode = form.mode === "Home Tuition";
    const isOnlineMode = form.mode === "Online";
    const indiaItem = countries.find((c) => (c.value || "").toLowerCase() === "india");

    const visibleCountries = isHomeMode && indiaItem ? [indiaItem] : countries;

    const countryObj = countries.find((c) => c.id === addrCountryId);
    const countryName = (countryObj?.value || "").trim();
    const isIndia = countryName.toLowerCase() === "india";
    const countryChosen = Boolean(addrCountryId);
    const isHome = isHomeMode || form.mode === "Both";

    // Auto-select India + lock when mode flips to Home Tuition
    if (isHomeMode && indiaItem && addrCountryId !== indiaItem.id) {
        // schedule the update (avoid setState during render via micro-task)
        Promise.resolve().then(() => {
            setAddrCountryId(indiaItem.id);
            setAddrStateId(""); setAddrCityId(""); setAddrPincodeId("");
            update("state", ""); update("city", ""); update("pin_code", "");
        });
    }

    // Country-change handler — when user picks a non-India country, force mode to Online
    const setCountry = (id, opt) => {
        if (isHomeMode) return;  // locked
        setAddrCountryId(id);
        setAddrStateId(""); setAddrCityId(""); setAddrPincodeId("");
        update("state", ""); update("city", ""); update("pin_code", "");
        const cname = (opt?.label || opt?.value || "").trim();
        if (cname && cname.toLowerCase() !== "india" && form.mode !== "Online") {
            setForm((f) => ({ ...f, mode: "Online" }));
        }
    };

    const buildIsoDatetime = () => {
        if (!form.preferred_demo_date) return null;
        let h = parseInt(form.preferred_demo_hour, 10);
        if (form.preferred_demo_ampm === "PM" && h < 12) h += 12;
        if (form.preferred_demo_ampm === "AM" && h === 12) h = 0;
        const hh = String(h).padStart(2, "0");
        return `${form.preferred_demo_date}T${hh}:${form.preferred_demo_minute}:00`;
    };
    const humanTiming = () => {
        if (!form.preferred_demo_date) return null;
        return `${form.preferred_demo_hour}:${form.preferred_demo_minute} ${form.preferred_demo_ampm}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const primary = form.students[0];
        if (!primary.name.trim() || !form.phone.trim() || !primary.grade || !primary.subjects.length) {
            toast.error("Student 1 name, parent phone, grade, and at least one subject are required.");
            return;
        }
        if (!isValidPhoneNumber(form.phone)) {
            toast.error("Please enter a valid phone number with country code.");
            return;
        }
        for (let i = 1; i < form.students.length; i += 1) {
            const s = form.students[i];
            if (!s.name.trim() || !s.grade || !s.subjects.length) {
                toast.error(`Student ${i + 1}: name, grade and at least one subject are required.`);
                return;
            }
        }
        // India = mandatory address / state / city / pincode
        if (isIndia) {
            if (!form.state.trim() || !form.city.trim() || !form.pin_code.trim()) {
                toast.error("For India, State, City and PIN Code are all required.");
                return;
            }
        }
        // Duplicate detection — best-effort
        try {
            const dc = await api.post("/admin/leads/check-duplicates", { phone: form.phone, email: form.email || null });
            if (dc.data?.count > 0) {
                const names = (dc.data.duplicates || []).slice(0, 3).map((m) => `${m.name || "—"} (${m.pipeline_stage || m.status || "?"})`).join(", ");
                const ok = window.confirm(
                    `⚠ Duplicate alert\n\n${dc.data.count} existing lead(s) with this phone or email already exist:\n${names}${dc.data.count > 3 ? "  …and more" : ""}\n\nCreate another lead anyway?`,
                );
                if (!ok) return;
            }
        } catch { /* duplicate check is best-effort */ }

        setSubmitting(true);
        try {
            const payload = {
                // Primary student fields land on the root for back-compat
                name: primary.name.trim(),
                parent_name: form.parent_name.trim() || null,
                email: form.email.trim() || null,
                phone: form.phone,
                phone_country: countryOf(form.phone) || null,
                grade: primary.grade,
                board: primary.board || null,
                subjects: primary.subjects.join(", "),
                wants_all_major_subjects: primary.subjects.includes(ALL_MAJOR_SUBJECTS),
                mode: form.mode,
                preferred_demo_at: buildIsoDatetime(),
                preferred_timing: humanTiming(),
                preferred_tutor_gender: primary.preferred_tutor_gender || "any",
                batch_type: primary.batch_type || null,
                class_duration_minutes: primary.class_duration_minutes || null,
                // Structured address
                address_house_no: form.address_house_no.trim() || null,
                address_area: form.address_area.trim() || null,
                address_landmark: form.address_landmark.trim() || null,
                country: countryName || null,
                city: form.city.trim() || null,
                state: form.state.trim() || null,
                pin_code: form.pin_code.trim() || null,
                area: form.address_area.trim() || null, // legacy mirror
                // Fees
                quoted_fee: form.quoted_fee !== "" ? Number(form.quoted_fee) : null,
                tutor_in_hand_fee: form.tutor_in_hand_fee !== "" ? Number(form.tutor_in_hand_fee) : null,
                fee_type: form.fee_type || null,
                // Notes
                special_notes: form.special_notes.trim() || null,
                additional_requirements: form.additional_requirements.trim() || null,
                // V24 — AI-first optional fields
                language_preference: languagePreference.length ? languagePreference : null,
                preferred_days: preferredDays.length ? preferredDays : null,
                preferred_time_window: form.preferred_time_window || null,
                exam_goal: form.exam_goal || null,
                curriculum_preference: form.curriculum_preference || null,
                learning_level: form.learning_level || null,
                special_requirements: form.special_requirements.trim() || null,
                // Pipeline
                status: form.status,
                lead_type: form.lead_type,
                // Siblings (everything beyond the first student)
                additional_students: form.students.slice(1).map((s) => ({
                    name: s.name.trim(),
                    grade: s.grade,
                    board: s.board || null,
                    subjects: s.subjects.join(", ") || null,
                    wants_all_major_subjects: s.subjects.includes(ALL_MAJOR_SUBJECTS),
                    mode: form.mode,
                    preferred_tutor_gender: s.preferred_tutor_gender || "any",
                    batch_type: s.batch_type || null,
                    class_duration_minutes: s.class_duration_minutes || null,
                    preferred_timing: humanTiming(),
                })),
            };
            const r = await api.post("/admin/leads/registrations", payload);
            toast.success(`Demo lead created for ${primary.name}${form.students.length > 1 ? ` + ${form.students.length - 1} sibling(s)` : ""}`);
            onCreated?.(r.data);
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create lead");
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" onClick={onClose}>
            <div
                className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="create-demo-lead-modal"
            >
                <div className="p-6 border-l-[5px] border-l-orange-500 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-2">
                        <Plus className="w-5 h-5 text-orange-600" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-700 font-bold">Manual Demo Lead</div>
                            <h3 className="font-heading text-xl font-bold text-[#0F172A]">Create Demo Registration</h3>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close" data-testid="create-lead-close" className="p-1 hover:bg-slate-100 rounded">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form className="p-6 space-y-6 text-sm" onSubmit={submit}>
                    <DataCompletenessMeter percent={completenessPct} kind="lead" testid="cdl-ai-meter" />
                    {/* Section: Parent / Contact + Mode + Lead Type */}
                    <Section title="Parent & Mode">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Lead Type">
                                <select value={form.lead_type} onChange={(e) => update("lead_type", e.target.value)} className={inputCls} data-testid="cdl-lead-type">
                                    <option value="demo">Demo Class Lead</option>
                                    <option value="direct_tuition">Direct Tuition (skip demo)</option>
                                </select>
                            </Field>
                            <Field label="Mode *">
                                <select
                                    value={form.mode}
                                    onChange={(e) => update("mode", e.target.value)}
                                    className={inputCls}
                                    data-testid="cdl-mode"
                                    disabled={countryChosen && !isIndia}
                                    title={countryChosen && !isIndia ? "Home Tuition is unavailable outside India" : undefined}
                                >
                                    {MODES.map((m) => (
                                        <option key={m} value={m} disabled={!isIndia && countryChosen && m !== "Online"}>
                                            {m}{!isIndia && countryChosen && m !== "Online" ? " (India only)" : ""}
                                        </option>
                                    ))}
                                </select>
                                {isHomeMode && (
                                    <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-1 flex items-start gap-1.5" data-testid="cdl-mode-note-home">
                                        🏠 <span>Home Tuition is currently available only within India.</span>
                                    </p>
                                )}
                                {isOnlineMode && (
                                    <p className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 mt-1 flex items-start gap-1.5" data-testid="cdl-mode-note-online">
                                        🌍 <span>1:1 Live Online is available for students across India and worldwide.</span>
                                    </p>
                                )}
                                {countryChosen && !isIndia && (
                                    <p className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 mt-1 flex items-start gap-1.5" data-testid="cdl-intl-mode-notice">
                                        <Globe2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                        <span>For international students ({countryName}), Anand Tutorials currently provides only <strong>1:1 Live Online Classes</strong>. Home Tuition is India-only.</span>
                                    </p>
                                )}
                            </Field>
                            <Field label="Parent Name">
                                <input value={form.parent_name} onChange={(e) => update("parent_name", e.target.value)} className={inputCls} data-testid="cdl-parent" />
                            </Field>
                            <Field label="Mobile Number *">
                                <PhoneInputE164 value={form.phone} onChange={(v) => update("phone", v)} required testid="cdl-phone" />
                            </Field>
                            <Field label="Email">
                                <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="cdl-email" />
                            </Field>
                            <Field label="Lead Status">
                                <select value={form.status} onChange={(e) => update("status", e.target.value)} className={inputCls} data-testid="cdl-status">
                                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                                </select>
                            </Field>
                        </div>
                    </Section>

                    {/* Section: Students (multi) */}
                    <Section
                        title={<><Users className="inline w-4 h-4 mr-1.5 -mt-0.5" />Students <span className="text-slate-500 font-normal">({form.students.length})</span></>}
                        action={(
                            <button
                                type="button"
                                onClick={addStudent}
                                data-testid="cdl-add-student"
                                className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 font-semibold"
                            >
                                <Plus className="w-3.5 h-3.5" /> Add More Student
                            </button>
                        )}
                    >
                        <p className="text-[11px] text-slate-500 mb-3 -mt-2">
                            One lead, multiple students under the same parent (e.g. siblings). AI will first look for a tutor who can teach all students together,
                            then fall back to per-student tutors if no combined match is suitable.
                        </p>
                        <div className="space-y-4">
                            {form.students.map((s, idx) => (
                                <div key={idx} data-testid={`cdl-student-card-${idx}`} className="rounded-xl border border-slate-200 p-4 bg-slate-50/40">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                            Student {idx + 1} {idx === 0 && <span className="text-[10px] font-normal text-slate-400">(Primary)</span>}
                                        </div>
                                        {idx > 0 && (
                                            <button type="button" onClick={() => removeStudent(idx)} data-testid={`cdl-remove-student-${idx}`} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
                                                <Trash2 className="w-3.5 h-3.5" /> Remove
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <Field label="Student Name *">
                                            <input value={s.name} onChange={(e) => updateStudent(idx, "name", e.target.value)} className={inputCls} data-testid={`cdl-name-${idx}`} />
                                        </Field>
                                        <Field label={<><GraduationCap className="inline w-3.5 h-3.5 mr-1" />Grade / Class *</>}>
                                            <SearchableSelect
                                                options={GRADES.map((g) => ({ value: g }))}
                                                value={s.grade}
                                                onChange={(v) => updateStudent(idx, "grade", v)}
                                                placeholder="Search grade (Class 5, JEE, …)"
                                                testid={`cdl-grade-${idx}`}
                                                required
                                            />
                                        </Field>
                                        <Field label="Board">
                                            <SearchableSelect
                                                options={BOARDS.map((b) => ({ value: b }))}
                                                value={s.board}
                                                onChange={(v) => updateStudent(idx, "board", v)}
                                                placeholder="Search board (CB → CBSE…)"
                                                testid={`cdl-board-${idx}`}
                                            />
                                        </Field>
                                        <Field label="Preferred Tutor Gender">
                                            <select value={s.preferred_tutor_gender} onChange={(e) => updateStudent(idx, "preferred_tutor_gender", e.target.value)} className={inputCls} data-testid={`cdl-gender-${idx}`}>
                                                <option value="any">Any</option>
                                                <option value="female">Female</option>
                                                <option value="male">Male</option>
                                            </select>
                                        </Field>
                                        <Field label="Batch Type">
                                            <select value={s.batch_type} onChange={(e) => updateStudent(idx, "batch_type", e.target.value)} className={inputCls} data-testid={`cdl-batch-${idx}`}>
                                                {BATCH_TYPES.map((b) => <option key={b}>{b}</option>)}
                                            </select>
                                        </Field>
                                        <Field label="Class Duration">
                                            <select value={s.class_duration_minutes} onChange={(e) => updateStudent(idx, "class_duration_minutes", Number(e.target.value))} className={inputCls} data-testid={`cdl-duration-${idx}`}>
                                                {DURATIONS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                                            </select>
                                        </Field>
                                        <div className="md:col-span-2">
                                            <Field label={<><BookOpen className="inline w-3.5 h-3.5 mr-1" />Subjects * <span className="text-slate-400 font-normal">(click to toggle)</span></>}>
                                                {primaryGrades.has(s.grade) && (
                                                    <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-1.5" data-testid={`cdl-allmajors-hint-${idx}`}>
                                                        Primary-level grade — pick <strong>✅ All Major Subjects</strong> below to flag this student for any tutor who can teach the core syllabus.
                                                    </p>
                                                )}
                                                <div className="flex flex-wrap gap-1.5" data-testid={`cdl-subjects-${idx}`}>
                                                    {studentSubjectOptions(s.grade).map((sub) => {
                                                        const on = s.subjects.includes(sub);
                                                        const isAllMajors = sub === ALL_MAJOR_SUBJECTS;
                                                        return (
                                                            <button
                                                                key={sub}
                                                                type="button"
                                                                onClick={() => toggleStudentSubject(idx, sub)}
                                                                data-testid={`cdl-subject-${idx}-${sub.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                                data-on={on}
                                                                data-allmajors={isAllMajors ? "true" : undefined}
                                                                className={`text-xs px-2.5 py-1 rounded-full border transition ${
                                                                    isAllMajors
                                                                        ? on
                                                                            ? "bg-emerald-600 border-emerald-600 text-white font-bold"
                                                                            : "bg-emerald-50 border-emerald-400 text-emerald-800 font-semibold hover:bg-emerald-100"
                                                                        : on
                                                                            ? "bg-orange-100 border-orange-400 text-orange-900 font-semibold"
                                                                            : "bg-white border-slate-200 text-slate-600 hover:border-orange-300"
                                                                }`}
                                                            >
                                                                {isAllMajors ? "✅ " : ""}{sub}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                {s.subjects.length > 0 && (
                                                    <div className="text-[11px] text-slate-500 mt-1">Selected: {s.subjects.join(", ")}</div>
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Section: Demo Schedule (12-hour format) */}
                    <Section title={<><Calendar className="inline w-4 h-4 mr-1.5 -mt-0.5" />Preferred Demo Date & Time</>}>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="col-span-2">
                                <Field label="Date">
                                    <input type="date" value={form.preferred_demo_date} onChange={(e) => update("preferred_demo_date", e.target.value)} className={inputCls} data-testid="cdl-date" />
                                </Field>
                            </div>
                            <Field label="Hour (12h)">
                                <select value={form.preferred_demo_hour} onChange={(e) => update("preferred_demo_hour", e.target.value)} className={inputCls} data-testid="cdl-hour">
                                    {HOURS_12.map((h) => <option key={h} value={h}>{h}</option>)}
                                </select>
                            </Field>
                            <Field label="Minute">
                                <select value={form.preferred_demo_minute} onChange={(e) => update("preferred_demo_minute", e.target.value)} className={inputCls} data-testid="cdl-minute">
                                    {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </Field>
                            <Field label="AM / PM">
                                <select value={form.preferred_demo_ampm} onChange={(e) => update("preferred_demo_ampm", e.target.value)} className={inputCls} data-testid="cdl-ampm">
                                    <option value="AM">AM</option>
                                    <option value="PM">PM</option>
                                </select>
                            </Field>
                        </div>
                    </Section>

                    {/* Section: Address (cascading Country → State → City → Pincode from Master Data) */}
                    <Section title={<><MapPin className="inline w-4 h-4 mr-1.5 -mt-0.5" />Address {countryChosen && !isIndia && <span className="text-[11px] font-normal text-blue-600">(optional for international leads)</span>}{countryChosen && isIndia && isHome && <span className="text-[11px] font-normal text-slate-500">(used for tutor proximity matching)</span>}</>}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Field label={`Country${isIndia || isHomeMode ? " *" : ""}`}>
                                <SearchableSelect
                                    options={visibleCountries.map((c) => ({ value: c.id, label: c.value }))}
                                    value={addrCountryId}
                                    onChange={setCountry}
                                    placeholder={isHomeMode ? "India (locked — Home Tuition is India-only)" : "Search country (Aus → Australia, USA, …)"}
                                    testid="cdl-country"
                                    required
                                    disabled={isHomeMode}
                                    allowClear={!isHomeMode}
                                />
                                {isHomeMode && (
                                    <p className="text-[10px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="cdl-home-india-lock">
                                        🏠 Home Tuition is currently available only within India.
                                    </p>
                                )}
                            </Field>
                            <Field label={`State${isIndia ? " *" : ""}`}>
                                <SearchableSelect
                                    options={states.map((s) => ({ value: s.id, label: s.value }))}
                                    value={addrStateId}
                                    onChange={(id, opt) => {
                                        setAddrStateId(id);
                                        setAddrCityId(""); setAddrPincodeId("");
                                        update("state", opt?.label || "");
                                        update("city", ""); update("pin_code", "");
                                    }}
                                    placeholder={addrCountryId ? "Search state…" : "Choose country first"}
                                    disabled={!addrCountryId}
                                    testid="cdl-state"
                                />
                            </Field>
                            <Field label={`City${isIndia ? " *" : ""}`}>
                                <SearchableSelect
                                    options={cities.map((c) => ({ value: c.id, label: c.value }))}
                                    value={addrCityId}
                                    onChange={(id, opt) => {
                                        setAddrCityId(id);
                                        setAddrPincodeId("");
                                        update("city", opt?.label || "");
                                        update("pin_code", "");
                                    }}
                                    placeholder={addrStateId ? "Search city (Nag → Nagpur, …)" : "Choose state first"}
                                    disabled={!addrStateId}
                                    testid="cdl-city"
                                />
                            </Field>
                            <Field label="House / Flat No.">
                                <input value={form.address_house_no} onChange={(e) => update("address_house_no", e.target.value)} className={inputCls} data-testid="cdl-house" />
                            </Field>
                            <Field label="Area / Colony">
                                <input value={form.address_area} onChange={(e) => update("address_area", e.target.value)} className={inputCls} data-testid="cdl-area" />
                            </Field>
                            <Field label="Landmark">
                                <input value={form.address_landmark} onChange={(e) => update("address_landmark", e.target.value)} className={inputCls} data-testid="cdl-landmark" />
                            </Field>
                            <Field label={`${isIndia ? "PIN Code *" : "Postal / ZIP Code"}`}>
                                <SearchableSelect
                                    options={pincodes.map((p) => ({ value: p.id, label: p.value }))}
                                    value={addrPincodeId}
                                    onChange={(id, opt) => {
                                        setAddrPincodeId(id);
                                        update("pin_code", opt?.label || "");
                                    }}
                                    placeholder={addrCityId ? (pincodes.length === 0 ? "No pincodes — type below" : "Search pincode (440 → 440001…)") : "Choose city first"}
                                    disabled={!addrCityId || pincodes.length === 0}
                                    testid="cdl-pincode-select"
                                />
                            </Field>
                            <Field label="…or type manually">
                                <input
                                    value={form.pin_code}
                                    onChange={(e) => { update("pin_code", e.target.value); setAddrPincodeId(""); }}
                                    className={inputCls}
                                    data-testid="cdl-pincode"
                                    placeholder={isIndia ? "e.g. 482002" : "Optional"}
                                />
                            </Field>
                        </div>
                    </Section>

                    {/* Section: Fees (admin-only, never on public site) */}
                    <Section title={<><Wallet className="inline w-4 h-4 mr-1.5 -mt-0.5" />Fee Information <span className="text-[11px] font-normal text-slate-500">(internal — never shown to parents)</span></>}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Field label="Quoted Fee (₹)">
                                <input type="number" min="0" value={form.quoted_fee} onChange={(e) => update("quoted_fee", e.target.value)} className={inputCls} data-testid="cdl-quoted-fee" placeholder="e.g. 8000" />
                            </Field>
                            <Field label="Tutor In-Hand Fee (₹)">
                                <input type="number" min="0" value={form.tutor_in_hand_fee} onChange={(e) => update("tutor_in_hand_fee", e.target.value)} className={inputCls} data-testid="cdl-tutor-fee" placeholder="e.g. 6000" />
                            </Field>
                            <Field label="Fee Type">
                                <select value={form.fee_type} onChange={(e) => update("fee_type", e.target.value)} className={inputCls} data-testid="cdl-fee-type">
                                    {FEE_TYPES.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
                                </select>
                            </Field>
                        </div>
                    </Section>

                    {/* Section: Notes */}
                    <Section title="Notes">
                        <div className="grid grid-cols-1 gap-3">
                            <Field label="Special Notes (for AI matching)">
                                <textarea
                                    rows={2}
                                    value={form.special_notes}
                                    onChange={(e) => update("special_notes", e.target.value)}
                                    placeholder="e.g. requires patient tutor, prefers NCERT, weak in algebra…"
                                    className={inputCls}
                                    data-testid="cdl-special-notes"
                                />
                            </Field>
                            <Field label="Additional Requirements (internal)">
                                <textarea
                                    rows={2}
                                    value={form.additional_requirements}
                                    onChange={(e) => update("additional_requirements", e.target.value)}
                                    placeholder="e.g. parent prefers female coordinator, mother is doctor, etc."
                                    className={inputCls}
                                    data-testid="cdl-additional"
                                />
                            </Field>
                        </div>
                    </Section>

                    {/* V24 — Optional AI fields for accurate tutor matching */}
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 overflow-hidden" data-testid="cdl-ai-section">
                        <button
                            type="button"
                            onClick={() => setAiOpen((v) => !v)}
                            data-testid="cdl-ai-toggle"
                            aria-expanded={aiOpen}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-emerald-100/40"
                        >
                            <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-900">
                                <Sparkles className="w-4 h-4 text-emerald-600" />
                                AI Matching — Optional Details
                                <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded">Improves match accuracy</span>
                            </span>
                            <span className="text-emerald-700 text-xs">{aiOpen ? "Hide" : "Show"}</span>
                        </button>
                        {aiOpen && (
                            <div className="px-4 pb-4 pt-1 space-y-3" data-testid="cdl-ai-fields">
                                <p className="text-[11px] text-emerald-800 -mt-1">
                                    Optional — helps improve AI tutor matching accuracy. Fill what you know; skip the rest.
                                </p>
                                <Field label="Preferred Language(s)">
                                    <MultiSelectChips
                                        options={languageItems.map((l) => ({ value: l.value }))}
                                        value={languagePreference}
                                        onChange={setLanguagePreference}
                                        placeholder="Search languages (English, Hindi, Tamil…)"
                                        testid="cdl-language"
                                        pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                                    />
                                </Field>
                                <Field label="Preferred Days">
                                    <MultiSelectChips
                                        options={PREFERRED_DAYS}
                                        value={preferredDays}
                                        onChange={setPreferredDays}
                                        placeholder="Search days…"
                                        testid="cdl-days"
                                        pillTone="bg-emerald-100 border-emerald-400 text-emerald-900"
                                    />
                                </Field>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <Field label="Preferred Time Window">
                                        <select value={form.preferred_time_window} onChange={(e) => update("preferred_time_window", e.target.value)} className={inputCls} data-testid="cdl-time-window">
                                            <option value="">Not specified</option>
                                            {PREFERRED_TIME_WINDOWS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Learning Level">
                                        <select value={form.learning_level} onChange={(e) => update("learning_level", e.target.value)} className={inputCls} data-testid="cdl-learning-level">
                                            <option value="">Not specified</option>
                                            {LEARNING_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Exam Goal">
                                        <SearchableSelect
                                            options={examItems.map((e) => ({ value: e.value }))}
                                            value={form.exam_goal}
                                            onChange={(v) => update("exam_goal", v)}
                                            placeholder="Boards, JEE, NEET, Olympiad…"
                                            testid="cdl-exam-goal"
                                            allowClear
                                        />
                                    </Field>
                                    <Field label="Curriculum Preference">
                                        <SearchableSelect
                                            options={currItems.map((c) => ({ value: c.value }))}
                                            value={form.curriculum_preference}
                                            onChange={(v) => update("curriculum_preference", v)}
                                            placeholder="NCERT, IB-DP, Cambridge…"
                                            testid="cdl-curriculum"
                                            allowClear
                                        />
                                    </Field>
                                </div>
                                <Field label="Special Requirements">
                                    <textarea
                                        rows={2}
                                        value={form.special_requirements}
                                        onChange={(e) => update("special_requirements", e.target.value)}
                                        placeholder="e.g. dyslexia-friendly tutor, prefers visual learning aids, weekend-only…"
                                        className={inputCls}
                                        data-testid="cdl-special-requirements"
                                    />
                                </Field>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="cdl-submit"
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 disabled:opacity-60"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            Create Demo Lead
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</span>
            {children}
        </label>
    );
}

function Section({ title, action, children }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-100">
                <h4 className="text-sm font-bold text-slate-800">{title}</h4>
                {action}
            </div>
            {children}
        </div>
    );
}
